import type { GridRowId } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import type { GridRow } from 'src/lib/gridRows.store';
import type { GridApiRef } from 'src/modules/grids';
import {
  type PalletTagPickerInput,
  PalletTagPickerModal,
  type PalletTagPickerSelection,
  transferPalletTagPicker,
} from 'src/modules/palletTags';

import type { LotTransferItemPalletTags, LotTransferItemRow } from './InventoryTransferGrid';

/**
 * The row whose tags are being edited, addressed the way the GRID addresses it.
 * `nestedParentId` is the auto-generated-nested-parent workaround.
 */
export type EditingTagsForRow = { id: GridRowId; nestedParentId?: GridRowId } | undefined;

type TransferPalletTagSelection = Pick<PalletTagPickerSelection, 'quantity' | 'palletTag'>;

type PalletTagModalRow = Pick<
  GridRow<LotTransferItemRow>,
  'rowId' | 'id' | 'lot' | 'unitsTransferred' | 'palletTags' | 'palletTagsOnTransferItem'
>;

type InventoryTransferPalletTagModalProps = {
  handleCloseTagsModal: () => void;
  handleSaveTransferredTags: (
    editingTagsForRow: EditingTagsForRow,
    palletTags: TransferPalletTagSelection[]
  ) => Promise<unknown>;
  editingTagsForRow: EditingTagsForRow;
  rows: readonly PalletTagModalRow[];
  apiRef: GridApiRef;
  getRowPalletTags: (row: PalletTagModalRow | null | undefined) => LotTransferItemPalletTags | null | undefined;
  canEdit: boolean;
};

/**
 * `editingTagsForRow.id` is always the GRID row id — which on an
 * EditableDataGrid surface is the store's `rowId`, not the DB id (ruling B1).
 * The saved-row lookup therefore matches on `rowId` first and only falls back to
 * `id` for the legacy AddableDataGrid shape, where `getRowId = row => row.id`
 * made the two identical.
 */
const resolveEditingRow = (
  editingTagsForRow: EditingTagsForRow,
  rows: readonly PalletTagModalRow[],
  apiRef: GridApiRef
): PalletTagModalRow | null | undefined => {
  if (!editingTagsForRow) {
    return null;
  }

  const targetRowId = editingTagsForRow.nestedParentId ?? editingTagsForRow.id;

  const isEditing = apiRef.current?.getRowMode(targetRowId) === 'edit';

  if (isEditing) {
    return apiRef.current?.getRowWithUpdatedValues(targetRowId, 'palletTags') as PalletTagModalRow;
  }

  // if the row's not in edit mode, always use the saved row as the SoT
  return rows.find((row) => (row.rowId ?? row.id) === editingTagsForRow.id);
};

const toPickerInputs = (
  palletTags: LotTransferItemPalletTags | null | undefined
): PalletTagPickerInput[] | null | undefined =>
  palletTags
    ? palletTags.flatMap((tag) =>
        tag
          ? [{ palletTag: { id: tag.palletTag.id, tagNumber: tag.palletTag.tagNumber ?? '' }, quantity: tag.quantity }]
          : []
      )
    : palletTags;

export default function InventoryTransferPalletTagModal({
  handleCloseTagsModal,
  handleSaveTransferredTags,
  editingTagsForRow,
  rows,
  apiRef,
  getRowPalletTags,
  canEdit,
}: InventoryTransferPalletTagModalProps) {
  const row = useMemo(() => resolveEditingRow(editingTagsForRow, rows, apiRef), [editingTagsForRow, rows, apiRef]);
  const lot = useMemo(
    () => ({ id: row?.lot?.id, slug: row?.lot?.slug ?? '', warehouseId: row?.lot?.warehouseId }),
    [row]
  );
  const initialInputs = useMemo(() => toPickerInputs(getRowPalletTags(row)), [row, getRowPalletTags]);

  return (
    <PalletTagPickerModal
      variant={transferPalletTagPicker}
      open={!!editingTagsForRow}
      lot={lot}
      fetchKey={row ?? undefined}
      initialInputs={initialInputs}
      unitsToCompare={row?.unitsTransferred}
      canEdit={canEdit}
      onClose={handleCloseTagsModal}
      onSave={(selections) =>
        handleSaveTransferredTags(
          editingTagsForRow,
          selections.map(({ quantity, palletTag }) => ({ quantity, palletTag }))
        )
      }
    />
  );
}
